'use client';

import { useMemo } from 'react';
import { useProjectPermissions } from '@frameio/permissions';
import { MoveToCopyTo } from '@frameio/project-tree';
import { useIsIntegrationClient } from '@frameio/shared/contexts/ClientNameContext';
import { layouts } from '@frameio/shared/contexts/PanelLayoutContext/layouts';
import type {
  PanelsBreakpointTemplate,
  PanelsTemplate,
} from '@frameio/shared/contexts/PanelLayoutContext/types';
import { useTemplateLayoutState } from '@frameio/shared/hooks';
import type { TemplateStoreAccordion } from '@frameio/shared/hooks/useLayoutState/types';
import type {
  ResizableAccordionTemplate,
  SerializedPanelState,
} from '@frameio/vapor';
import {
  Panel,
  Panels,
  useActiveWindowBreakpoint,
  useMeasure,
} from '@frameio/vapor';
import { StaticAssets } from './StaticAssets';
import styles from './StaticProjectNav.module.css';
import { StaticShares } from './StaticShares';

type Props = {
  projectId: string;
  rootFolderId?: string;
};

const PANEL_ROW_MIN_HEIGHT = 200;
const DEFAULT_ROWS: ResizableAccordionTemplate = [{ size: 50 }, { size: 50 }];

/**
 * Row sizes are persisted as a percent of the container's own height (0-100),
 * not the window, since this column can be shorter than the viewport.
 * Vapor's Panels grid only understands pixels/'1fr', so we convert percent to
 * pixels here using the measured container height.
 */
function buildPanelsTemplate(
  rows: ResizableAccordionTemplate,
  containerHeight: number
): PanelsTemplate {
  const breakpointTemplate: PanelsBreakpointTemplate = {
    areas: [['assets'], ['shares']],
    resizers: { assets: 'bottom' },
    columns: [{ key: 'assets', defaultSize: '1fr', isToggleable: false }],
    rows: [
      {
        key: 'assets',
        defaultMin: PANEL_ROW_MIN_HEIGHT,
        defaultSize: Math.round(
          ((rows[0]?.size ?? 50) / 100) * containerHeight
        ),
        isToggleable: false,
      },
      {
        key: 'shares',
        defaultMin: PANEL_ROW_MIN_HEIGHT,
        defaultSize: Math.round(
          ((rows[1]?.size ?? 50) / 100) * containerHeight
        ),
        isToggleable: false,
      },
    ],
  };

  return {
    base: breakpointTemplate,
    tablet: breakpointTemplate,
    desktop: breakpointTemplate,
    visible: ['assets', 'shares'],
  };
}

export function StaticProjectNav({ projectId, rootFolderId }: Props) {
  const isIntegrationClient = useIsIntegrationClient();
  const canShare =
    useProjectPermissions({ check: 'canShare', projectId }) ?? false;
  // the share links pages only exist in the web app, so linking to them
  // from the integrations panel would 404
  const showShares = canShare && !isIntegrationClient;

  const breakpoint = useActiveWindowBreakpoint();
  const [containerRef, containerBounds] = useMeasure();
  const containerHeight = containerBounds.height;

  const { template: storedTemplate, setTemplate: persistRows } =
    useTemplateLayoutState('staticProjectNavigation');

  const rows =
    (storedTemplate as TemplateStoreAccordion | null)?.rows ?? DEFAULT_ROWS;

  // Before the ResizeObserver reports a real size, containerHeight is 0.
  // Building pixel row sizes from that would clamp both rows to their
  // PANEL_ROW_MIN_HEIGHT floor (double the min height, ignoring the actual
  // space available), so fall back to the '1fr' base template until we
  // have a real measurement to convert the persisted percentages from.
  const panelsTemplate = useMemo(
    () =>
      containerHeight
        ? buildPanelsTemplate(rows, containerHeight)
        : layouts.staticProjectNavigation.getPanelState(['assets', 'shares']),
    [rows, containerHeight]
  );

  const handleTemplateChange = (state: SerializedPanelState) => {
    const [assets, shares] = state[breakpoint].rows;
    const assetsSize = assets.size ?? 0;
    const sharesSize = shares.size ?? 0;
    const total = assetsSize + sharesSize;

    if (!total) return;

    persistRows({
      rows: [
        { size: Math.round((assetsSize / total) * 100) },
        { size: Math.round((sharesSize / total) * 100) },
      ],
    });
  };

  return (
    <div ref={containerRef} className={styles.container}>
      {showShares ? (
        <Panels
          onTemplateChange={handleTemplateChange}
          template={panelsTemplate}
        >
          <Panel area="assets">
            <StaticAssets projectId={projectId} rootFolderId={rootFolderId} />
          </Panel>
          <Panel area="shares">
            <StaticShares projectId={projectId} />
          </Panel>
        </Panels>
      ) : (
        <StaticAssets projectId={projectId} rootFolderId={rootFolderId} />
      )}
      <MoveToCopyTo />
    </div>
  );
}
